import { useState, useEffect, createContext, useContext, ReactNode, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { linkBrowserToAccount } from '@/lib/fpAccountLink';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, displayName?: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const checkingForcedSignOut = useRef(false);
  const navigate = useNavigate();

  const checkForcedMemberSignOut = async (currentSession: Session | null) => {
    if (!currentSession || checkingForcedSignOut.current) return;
    checkingForcedSignOut.current = true;

    try {
      const { data, error } = await supabase.functions.invoke('member-session-status', {
        headers: { Authorization: `Bearer ${currentSession.access_token}` },
        body: {},
      });

      if (!error && data?.forceSignOut === true) {
        await supabase.auth.signOut({ scope: 'local' });
        setIsAdmin(false);
        toast.info('Votre session a expiré. Veuillez vous reconnecter.');
        navigate('/espace-membre');
      }
    } catch (error) {
      console.debug('Forced member sign-out check skipped:', error);
    } finally {
      checkingForcedSignOut.current = false;
    }
  };

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Check admin status when session changes
        if (session?.user) {
          setTimeout(() => {
            checkAdminStatus(session.user.id);
          }, 0);
          // Browser <-> account association: for a newly established session
          // and for an existing session restored at load, never on a token
          // refresh (guarded again per auth session in the helper).
          if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
            setTimeout(() => {
              linkBrowserToAccount(session.access_token, session.user.id);
            }, 0);
          }
        } else {
          setIsAdmin(false);
        }
      }
    );

    // Check for existing session
    const initializeAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        await checkAdminStatus(session.user.id);
      }
      setLoading(false);
    };
    
    initializeAuth();

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) return;

    checkForcedMemberSignOut(session);
    const intervalId = window.setInterval(() => {
      checkForcedMemberSignOut(session);
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, [session?.access_token]);

  const checkAdminStatus = async (userId: string): Promise<boolean> => {
    const { data, error } = await supabase.rpc('has_role', {
      _user_id: userId,
      _role: 'admin',
    });

    const isUserAdmin = !error && data === true;
    setIsAdmin(isUserAdmin);
    return isUserAdmin;
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast.error(error.message);
      return { error };
    }

    toast.success('Connexion réussie !');
    navigate('/admin');
    return { error: null };
  };

  const signUp = async (email: string, password: string, displayName?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          display_name: displayName || email.split('@')[0],
        },
      },
    });

    if (error) {
      if (error.message.includes('already registered')) {
        toast.error('Cet email est déjà utilisé');
      } else {
        toast.error(error.message);
      }
      return { error };
    }

    toast.success('Compte créé avec succès !');
    navigate('/admin');
    return { error: null };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      setIsAdmin(false);
      toast.success('Déconnexion réussie');
      navigate('/');
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};